import { cn } from '@recipes/ui';
import { Monitor, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { announce } from '@/components/feedback/live-region';
import { nextColorMode } from '@/lib/appearance/appearance';
import { useAppearance, useChooseAppearance } from './use-appearance';

const ICONS = { LIGHT: Sun, DARK: Moon, SYSTEM: Monitor } as const;

/**
 * The quick switch next to the language button: Light → Dark → System. The icon shows the
 * mode that is chosen, the label says what a press does. Themes are chosen in the profile.
 */
export function ColorModeToggle({
  variant,
  className,
}: {
  /** `labelled`: sidebar and start pages. `icon`: the corner of a phone screen. */
  variant: 'labelled' | 'icon';
  className?: string;
}) {
  const { t } = useTranslation();
  const { colorMode } = useAppearance();
  const choose = useChooseAppearance();
  const next = nextColorMode(colorMode);
  const Icon = ICONS[colorMode];

  return (
    <button
      type="button"
      onClick={() => {
        void choose({ colorMode: next });
        announce(t('appearance.mode.changed', { mode: t(`appearance.mode.${next}`) }));
      }}
      aria-label={t('appearance.mode.toggle', {
        mode: t(`appearance.mode.${colorMode}`),
        next: t(`appearance.mode.${next}`),
      })}
      data-testid="color-mode-toggle"
      data-mode={colorMode}
      className={cn(
        'inline-flex touch-target items-center justify-center gap-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        variant === 'labelled' ? 'px-3 py-2' : 'size-11 bg-background/80 backdrop-blur',
        className,
      )}
    >
      <Icon className="size-5" aria-hidden />
      {variant === 'labelled' && <span>{t(`appearance.mode.${colorMode}`)}</span>}
    </button>
  );
}
